/**
 * Shared document styling for the two ways a note leaves the app as a
 * formatted document: Print (browser/Tauri, `printHelpers.ts`) and Export
 * (weasyprint PDF/DOCX, `build-export-content.ts`).
 *
 * Both used to keep their own copy of these values, which let them drift —
 * the exported PDF ended up serif + loosely spaced while Print was
 * sans-serif + compact. Keeping the font stack and base rules here means the
 * two paths can never diverge again.
 */

/**
 * Sans-serif stack with broad CJK coverage. The trailing generic
 * `sans-serif` guarantees a non-serif result even where no named family is
 * installed (e.g. the weasyprint host).
 */
export const DOCUMENT_FONT_STACK =
  "'Noto Sans', 'Noto Sans SC', 'Noto Sans TC', 'Noto Sans JP', 'Noto Sans KR', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Microsoft YaHei', 'Heiti SC', 'Source Han Sans', 'Arial Unicode MS', sans-serif";

/**
 * The agreed document body styling: sans-serif font, an 18px medium heading,
 * compact section/list spacing, and bordered/shaded/full-width tables. The h1
 * size mirrors the ml-scribe weasyprint export so Print and Export headings
 * can't drift. Excludes page-level rules
 * (`@page`, body margins, `@media print`) so each consumer can apply its own
 * paging — weasyprint owns `@page` on Export; Print sets it for the browser.
 * `p.para` / `ul.list` cover the legacy markdown print path; bare `p` / `ul`
 * cover copy-settings-transformed editor HTML.
 */
/**
 * The body font size used by the default export (and the baseline against
 * which the `h1` size is kept proportional: default 16px body → 18px h1).
 */
export const DEFAULT_DOCUMENT_FONT_SIZE_PX = 16;

/**
 * Builds the document body CSS for a given font stack + base size. Keeping
 * this a function (rather than a frozen string) lets the export flow honour a
 * user-chosen font/size while the Print path keeps the original defaults via
 * `DOCUMENT_BASE_CSS` below. The `h1` size tracks the body size (+2) so the
 * heading scales with the chosen base instead of staying a fixed 18px.
 *
 * `h2`–`h5` are the markdown-native content headings. Their offsets mirror the
 * editor scale (24/20/16/14px against its 14px body) as deltas off the body
 * size, with the editor's medium weight and tightened leading/tracking on
 * h2–h4; h5 matches the section-header (h1-adjacent) treatment: body-sized,
 * default leading. Margins follow the compact paragraph rhythm.
 *
 * Print keeps `px` (browser 16px ≈ 12pt). Export passes `unit: 'pt'` so the
 * picker number is the size Word/PDF readers report.
 */
export const buildDocumentBaseCss = ({
  fontStack = DOCUMENT_FONT_STACK,
  fontSize = DEFAULT_DOCUMENT_FONT_SIZE_PX,
  unit = 'px',
}: {
  fontStack?: string;
  fontSize?: number;
  unit?: 'px' | 'pt';
} = {}): string => `
  body {
    font-family: ${fontStack};
    font-size: ${fontSize}${unit};
    line-height: 1.3;
    color: #222;
  }
  h1 { font-size: ${fontSize + 2}${unit}; font-weight: 500; }
  h2 { font-size: ${fontSize + 10}${unit}; }
  h3 { font-size: ${fontSize + 6}${unit}; }
  h4 { font-size: ${fontSize + 2}${unit}; }
  h5 { font-size: ${fontSize}${unit}; }
  h2, h3, h4 { line-height: 1.25; letter-spacing: -0.025em; }
  h2, h3, h4, h5 { font-weight: 500; margin: 0 0 12px 0; }
  p.para, p { margin: 0 0 12px 0; padding: 0; }
  ul.list, ul, ol { margin: 0 0 12px 20px; padding: 0; }
  ul.list li, ul li, ol li { margin: 0 0 10px 0; }
  strong { font-weight: 700; }
  em { font-style: italic; }
  hr { border: 0; border-top: 1px solid #bbb; margin: 10px 0; }
  br { display: block; margin: 0 0 10px 0; content: " "; line-height: 1.2; }
  table { border-collapse: collapse; width: 100%; margin: 1em 0; }
  table, th, td { border: 1px solid #ddd; }
  th, td { padding: 8px; text-align: left; }
  th { background-color: #F2F2F2; font-weight: bold; }
`;

export const DOCUMENT_BASE_CSS = buildDocumentBaseCss();
